import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchMacbook, createOrder } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, Shield, Laptop, Loader2, Package } from "lucide-react";

const conditionStyles = {
  excellent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  very_good: "bg-blue-50 text-blue-700 border-blue-200",
  good: "bg-amber-50 text-amber-700 border-amber-200",
};
const conditionLabels = { excellent: "Excellent", very_good: "Very Good", good: "Good" };
const tierLabels = { basic: "Basic", warranty: "With Extended Warranty" };
const categoryLabels = { air: "MacBook Air", pro: "MacBook Pro" };

const WHATS_INCLUDED = {
  basic: ["MacBook", "Charger"],
  warranty: ["MacBook", "Charger", "90-day warranty"],
};

export default function ProductDetailPage() {
  const { id } = useParams();
  const [macbook, setMacbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyForm, setBuyForm] = useState({ customer_name: "", customer_email: "" });
  const [buying, setBuying] = useState(false);
  useEffect(() => {
    fetchMacbook(id)
      .then((res) => setMacbook(res.data))
      .catch(() => toast.error("MacBook not found"))
      .finally(() => setLoading(false));
  }, [id]);

  const getImageUrl = (url) => {
    if (!url) return null;
    return url;
  };

  const handleBuy = async () => {
    if (!buyForm.customer_name || !buyForm.customer_email) {
      toast.error("Please fill in all fields");
      return;
    }
    setBuying(true);
    try {
      await createOrder({ macbook_id: id, ...buyForm });
      toast.success("Purchase successful! This MacBook is now yours.");
      setBuyOpen(false);
      setMacbook((prev) => ({ ...prev, status: "sold" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed");
    } finally {
      setBuying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="detail-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#86868b]" />
      </div>
    );
  }

  if (!macbook) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4" data-testid="detail-not-found">
        <p className="text-[#86868b]">MacBook not found</p>
        <Button asChild variant="outline" className="rounded-full">
          <Link to="/shop">Back to Shop</Link>
        </Button>
      </div>
    );
  }

  const isSold = macbook.status === "sold";
  const included = WHATS_INCLUDED[macbook.tier] || WHATS_INCLUDED.basic;

  return (
    <div data-testid="product-detail-page" className="py-8 sm:py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Back */}
        <Link
          to="/shop"
          data-testid="back-to-shop-link"
          className="inline-flex items-center gap-1.5 text-sm text-[#86868b] hover:text-[#1d1d1f] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Shop
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Photos */}
          <div data-testid="product-gallery">
            <div className="aspect-[4/3] rounded-2xl bg-[#f5f5f7] overflow-hidden mb-4">
              {macbook.photos?.length > 0 ? (
                <img
                  src={getImageUrl(macbook.photos[activePhoto])}
                  alt={categoryLabels[macbook.category]}
                  className="w-full h-full object-cover"
                  data-testid="product-main-image"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Laptop className="w-24 h-24 text-gray-300" />
                </div>
              )}
            </div>
            {macbook.photos?.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {macbook.photos.map((photo, i) => (
                  <button
                    key={i}
                    onClick={() => setActivePhoto(i)}
                    data-testid={`gallery-thumb-${i}`}
                    className={`gallery-thumb shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                      i === activePhoto ? "active border-[#1d1d1f]" : "border-transparent"
                    }`}
                  >
                    <img
                      src={getImageUrl(photo)}
                      alt={`View ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div data-testid="product-info">
            {/* Category & Tier */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-semibold text-[#86868b] uppercase tracking-wider">
                {categoryLabels[macbook.category]}
              </span>
              <span className="text-xs text-gray-300">|</span>
              <span className="text-xs font-semibold text-[#86868b] uppercase tracking-wider">
                {tierLabels[macbook.tier]}
              </span>
            </div>

            {/* Condition */}
            <Badge
              data-testid="product-condition-badge"
              className={`mb-4 text-xs font-medium border ${conditionStyles[macbook.condition] || "bg-gray-50 text-gray-700"}`}
            >
              {conditionLabels[macbook.condition] || macbook.condition} Condition
            </Badge>

            {/* Description */}
            {macbook.short_description && (
              <p className="text-base text-[#86868b] mb-6 leading-relaxed" data-testid="product-description">
                {macbook.short_description}
              </p>
            )}

            {/* Sold Message */}
            {isSold && (
              <div
                data-testid="sold-message"
                className="p-6 rounded-2xl bg-gray-50 border border-gray-100 mb-6"
              >
                <p className="text-lg font-semibold text-[#1d1d1f]">This MacBook has been sold</p>
                <p className="mt-1 text-sm text-[#86868b]">
                  Check out our other available MacBooks.
                </p>
                <Button
                  asChild
                  className="mt-4 rounded-full bg-[#1d1d1f] hover:bg-[#1d1d1f]/90"
                  data-testid="browse-others-btn"
                >
                  <Link to="/shop">Browse Available MacBooks</Link>
                </Button>
              </div>
            )}

            {/* Price & Buy */}
            {!isSold && (
              <div className="mb-6">
                <span data-testid="product-price" className="text-3xl font-bold text-[#1d1d1f]">
                  ${macbook.price?.toLocaleString()}
                </span>
                <div className="mt-4">
                  <Button
                    size="lg"
                    className="rounded-full px-10 h-12 text-sm font-semibold bg-[#1d1d1f] hover:bg-[#1d1d1f]/90 w-full sm:w-auto"
                    onClick={() => setBuyOpen(true)}
                    data-testid="buy-now-btn"
                  >
                    Buy Now
                  </Button>
                </div>
              </div>
            )}

            <Separator className="my-6" />

            {/* What's Included */}
            <div data-testid="whats-included">
              <h3 className="text-sm font-semibold text-[#1d1d1f] mb-4">What's Included</h3>
              <div className="space-y-3">
                {included.map((item, i) => (
                  <div key={i} className="flex items-center gap-3" data-testid={`included-item-${i}`}>
                    {item.includes("warranty") ? (
                      <Shield className="w-4 h-4 text-emerald-600 shrink-0" />
                    ) : (
                      <Package className="w-4 h-4 text-[#86868b] shrink-0" />
                    )}
                    <span className="text-sm text-[#1d1d1f]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <Separator className="my-6" />

            {/* Trust Signals */}
            <div className="space-y-3" data-testid="product-trust-signals">
              {[
                "Professionally tested & verified",
                "Clean macOS install",
                "Ships within 1-2 business days",
              ].map((text, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="text-sm text-[#86868b]">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Buy Dialog */}
      <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
        <DialogContent className="sm:max-w-md" data-testid="buy-dialog">
          <DialogHeader>
            <DialogTitle>Complete Your Purchase</DialogTitle>
            <DialogDescription>
              {categoryLabels[macbook.category]} — {tierLabels[macbook.tier]} — ${macbook.price?.toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Full Name</label>
              <Input
                data-testid="buy-name-input"
                placeholder="John Doe"
                value={buyForm.customer_name}
                onChange={(e) => setBuyForm((p) => ({ ...p, customer_name: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1d1d1f] block mb-1.5">Email Address</label>
              <Input
                data-testid="buy-email-input"
                type="email"
                placeholder="john@example.com"
                value={buyForm.customer_email}
                onChange={(e) => setBuyForm((p) => ({ ...p, customer_email: e.target.value }))}
              />
            </div>
            <p className="text-xs text-[#86868b]">
              Stripe checkout is not yet connected. This will simulate a purchase and mark the item as sold.
            </p>
            <Button
              className="w-full rounded-full h-11 font-semibold bg-[#1d1d1f] hover:bg-[#1d1d1f]/90"
              onClick={handleBuy}
              disabled={buying}
              data-testid="confirm-purchase-btn"
            >
              {buying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {buying ? "Processing..." : "Confirm Purchase"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
